import React, { useEffect, useState } from "react";
import { Card, Table, DatePicker, Typography, message, Grid, Spin, Col, Row, Tooltip, Button, Input, Space, Empty } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import appSettings from "apps/services/config";
import moment, { Moment } from "moment";
import MainDashboard from "libs/pages/warehouse-management/warehouse-components/warehouse-dashboard/src/lib/main-dashboard";
import { MainDashboardService, WarehouseDashboardService } from "@gtpl/shared-services/analytics";
import { DateRangeReq } from "@gtpl/shared-models/analytics-data";
import { FilterDropdownProps } from "antd/lib/table/interface";
import { ColumnType } from "antd/lib/table";
import { CalendarOutlined, SearchOutlined, } from "@ant-design/icons";
const { RangePicker } = DatePicker;
const { Title } = Typography;
const { useBreakpoint } = Grid;

const defaultMockData = {
  stockIn: 0,
  stockOut: 0,
  closeStock: 0,
  looseIn: 0,
  looseOut:0,
  deadStock: [],
  detailedStock: [],
};

const WarehouseDashboard = () => {
  const [dateRange, setDateRange] = useState<[Moment, Moment] | null>([
    moment().subtract(2, 'days'),
    moment().subtract(1, 'days'),
  ]);
  const [loading, setLoading] = useState(false);
  const [warehouseData, setWarehouseData] = useState(defaultMockData);
  const [ deadStockData, setDeadStockData ] = useState([]);
  const screens = useBreakpoint();
  const service = new MainDashboardService()
  const [deadStockLoading, setDeadStockLoading] = useState(false);

  const getColumnSearchProps = (dataIndex: string): ColumnType<any> => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: FilterDropdownProps) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => confirm()}
          style={{ width: 188, marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => confirm()}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters?.()}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ?.toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
  });

  const colors = {
    primary: '#2D3A4B',
    secondary: '#4A90E2',
    accent: '#FF6B6B',
    background: '#F8F9FA',
    textSecondary: '#6C757D',
    success: '#28A745',
    warning: '#FFC107',
    headerBg: 'powderblue',
    headerText: '#FFFFFF',
    cardHover: '#F8F9FA'
  };

  const cardStyles = {
    main: {
        borderRadius: '12px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
        border: '1px solid #e8e8e8'
    },
    header: {
        background: colors.headerBg,
        color: colors.headerText,
        borderRadius: '12px 12px 0 0',
        padding: '0px',
        fontSize: '16px',
        fontWeight: 600,
    }
  };

  const getDeadStock = () => {
    setDeadStockLoading(true);
    service.getDeadStock()
      .then((res) => {
        if (res?.status) {
          setDeadStockData(res.data);
          console.log("Dead stock data:", res.data);
        } else {
          setDeadStockData([]);
          message.error("Failed to load dead stock data");
        }
      })
      .catch((error) => {
        setDeadStockData([]);
        console.error("Error fetching dead stock:", error);
        message.error("An error occurred while fetching dead stock data");
      })
      .then(() => {
        setDeadStockLoading(false);
      });
  };
  
  

  const fetchStockCount = async (fromDate, toDate) => {
    try {
      const req = new DateRangeReq();
      req.fromDate = fromDate;
      req.toDate = toDate;
  
      const { status, data = [] } = await service.getStockCount(req);
  
      if (status) {
        const stockIn = data.find(item => item.stockInQty)?.stockInQty || {};
        const stockOut = data.find(item => item.stockOutQty)?.stockOutQty || {};
  
        return {
          stockIn: parseInt(stockIn.cases) || 0,
          stockOut: parseInt(stockOut.cases) || 0,
          looseIn: parseInt(stockIn.loosePouches) || 0,
          looseOut: parseInt(stockOut.loosePouches) || 0,
          closeStock: 0,
        };
      }
    } catch (error) {
      console.error("Error fetching stock count:", error);
      message.error("Failed to load stock data");
    }
  
    return { stockIn: 0, stockOut: 0, looseIn: 0, looseOut: 0, closeStock: 0 };
  };
  

  const fetchClosingStock = async () => {
    try {
      const response = await axios.post(
        `${appSettings.anlytics_url}` + `/mainDashboard/getClosingStock`
      );

      if (response.data?.status) {
        // Handle null or undefined values
        return response.data.data[0]?.ClosingStock || 0;
      }
    } catch (error) {
      console.error("Error fetching closing stock:", error);
      message.error("Failed to load closing stock data");
    }
    return 0;
  };

  const fetchDetailedStock = async () => {
    try {
      const response = await axios.post(
        `${appSettings.anlytics_url}` + `/mainDashboard/getDetailStock`
        ,
        {} // No payload required
      );

      if (response.data?.status) {
        return response.data.data.map(item => ({
          name: item.NAME,
          grade: item.grade,
          count: item.COUNT,
          loosePouches: item.loosePouches,
          poNo: item.poNo,
          packStyle: item.packStyle,
          transactionType: item.transactionType,
        }));
      }
    } catch (error) {
      console.error("Error fetching detailed stock:", error);
      message.error("Failed to load detailed stock data");
    }
    return [];
  };

  const handleDateChange = (dates, dateStrings) => {
    setDateRange(dates);
    if (dateStrings[0] && dateStrings[1]) {
      fetchData(dateStrings[0], dateStrings[1]);
    }
  };

  const fetchData = async (fromDate, toDate) => {
    setLoading(true);
    try {
      const [stockCount, closingStock, detailedStock] = await Promise.all([
        fetchStockCount(fromDate, toDate),
        fetchClosingStock(),
        fetchDetailedStock()
      ]);

      setWarehouseData({
        ...stockCount,
        closeStock: closingStock,
        detailedStock,
        deadStock: [
          { count: 3 },
          { count: 1 },
          { count: 1 },
        ],
        looseIn: stockCount.looseIn || 0,
        looseOut: stockCount.looseOut || 0,
      });
    } catch (error) {
      console.error("Error fetching warehouse data:", error);
      setWarehouseData(defaultMockData);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const defaultFromDate = dayjs().subtract(2, 'day').format('YYYY-MM-DD');
    const defaultToDate = dayjs().format('YYYY-MM-DD');
    fetchData(defaultFromDate, defaultToDate);
    getDeadStock()
  }, []);

  const columns1: any = [
    {
      title: "Product",
      dataIndex: "variety",
      key: "variety",
      ...getColumnSearchProps("variety")
    },
    { title: "30-60 days", dataIndex: "thirtyTo60", key: "thirtyTo60", align: 'right', render: (text: number) => text?.toLocaleString(), },
    { title: "60-90 days", dataIndex: "sixtyTo90", key: "sixtyTo90", align: 'right', render: (text: number) => text?.toLocaleString(), },
    { title: "90-120 days", dataIndex: "ninetyTo120", key: "ninetyTo120", align: 'right', render: (text: number) => text?.toLocaleString(), },
  ];
  
  
const columns2: any = [
  {
    title: "FG Product",
    dataIndex: "name",
    key: "name",
    ...getColumnSearchProps("name"),
  },
  {
    title: "PO No",
    dataIndex: "poNo",
    key: "poNo",
    align: 'center',
    ...getColumnSearchProps("poNo"),
  },
  {
    title: "Pack Style",
    dataIndex: "packStyle",
    key: "packStyle",
    align: 'center',
    ...getColumnSearchProps("packStyle"),
  },
  {
    title: "Transaction Type",
    dataIndex: "transactionType",
    key: "transactionType",
    align: 'center',
    ...getColumnSearchProps("transactionType"),
  },
  {
    title: "No of Cartons",
    dataIndex: "count",
    key: "count",
    align: "right",
    render: (text: number) => text?.toLocaleString(),
  },
  {
    title: "Loose Pouches",
    dataIndex: "loosePouches",
    key: "loosePouches",
    align: "right",
    render: (text: number) => text?.toLocaleString(),
  },
];


return (
  <div style={{ padding: '8px', background: colors.background }}>
    <Row justify="space-between" align="middle" style={{ marginBottom: 6 }}>
      <Col>
        <Title level={3} style={{ margin: 0, color: colors.primary }}>
          Warehouse Dashboard
        </Title>
        {/* <Typography.Text type="secondary" style={{ color: colors.textSecondary }}>
          Real-time inventory insights and analytics
        </Typography.Text> */}
      </Col>

      <Col>
        <Row gutter={16} align="middle">
          {/* Date Picker Column */}
          <Col>
            <Tooltip title="Date range applies only to Stock In & Out">
              {/* <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}> */}
                <RangePicker
                  onChange={handleDateChange}
                  // size={screens.xs ? "middle" : "small"}
                  style={{ width: '100%', maxWidth: 230 }}
                  allowClear={false}
                  value={dateRange}
                  suffixIcon={<CalendarOutlined style={{ color: colors.textSecondary }} />}
                />
              {/* </div> */}
            </Tooltip>
          </Col>

          {/* Cards Column */}
          <Col>
            <Row gutter={16}>
              {[
                { 
                  title: "Stock In", 
                  value: warehouseData.stockIn, 
                  loose: warehouseData.looseIn,
                  color: colors.primary 
                },
                { 
                  title: "Stock Out", 
                  value: warehouseData.stockOut, 
                  loose: warehouseData.looseOut,
                  color: colors.success 
                },
              ].map((card, index) => (
                <Col key={index}>
                  <Card
                    headStyle={{
                      background: card.color,
                      color: '#fff',
                      fontSize: '14px',
                      padding: '0 8px',
                      height: 36,
                      // lineHeight: '36px',
                      textAlign: 'center',
                      border: 'none',
                      borderTopLeftRadius: 12,
                      borderTopRightRadius: 12,
                    }}
                    style={{
                      width: 160,
                      marginBottom: 0,
                      borderRadius: 12,
                      overflow: 'hidden',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                    }}
                    bodyStyle={{
                      padding: '8px',
                      textAlign: 'center',
                      fontSize: '16px',
                      fontWeight: 600,
                    }}
                    title={card.title}
                  >
                    <div>{card?.value?.toLocaleString()}</div> 
                    {/* <span style={{fontSize: 10 }}>in kgs</span> */}
                    <div style={{ 
                      fontSize: '12px', 
                      marginTop: '4px', 
                      color: colors.warning,
                      lineHeight: 1.2
                    }}>
                      Loose Pouches: {card.loose}
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          </Col>
        </Row>
      </Col>
    </Row>

    <Spin spinning={loading}>
      <Row gutter={16}>
        <Col xs={24} md={14}>
          <Card
            title="Detailed Stock Analysis"
            headStyle={{
              background: 'powderblue',
              padding: '3px 6px',
              lineHeight: '0.2px',
              fontSize: 14,
              fontWeight: 600,
              borderRadius: '8px 8px 0 0',
              borderBottom: 0
            }}
            style={cardStyles.main}
            bodyStyle={{ padding: '16px' }}
            extra={
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingRight: 16 }}>
                <Typography.Text 
                  strong 
                  style={{ 
                    color: "black", 
                    padding: '4px 8px',
                    whiteSpace: 'nowrap'
                  }}
                >
                  Total Close Stock: {warehouseData.closeStock} on {dayjs().subtract(1, 'day').format('DD MMM YYYY')}
                </Typography.Text>
              </div>
            }
          >
            {warehouseData.detailedStock.length > 0 ? (
              <Table
              dataSource={warehouseData.detailedStock}
              columns={columns2}
              pagination={false}
              scroll={{ x: 200, y: 300 }}
              size="middle"
              bordered
              rowClassName={() => 'hover-row'}
            />
            ) : (
              <Empty description="No records found" />
            )}
          </Card>
        </Col>

        <Col xs={24} md={10}>
          <Spin spinning={deadStockLoading}>
            <Card
              title="Dead Stock Analysis"
              headStyle={{
                background: 'powderblue',
                padding: '3px 6px',
                lineHeight: '0.2px',
                fontSize: 14,
                fontWeight: 600,
                borderRadius: '8px 8px 0 0',
                borderBottom: 0
              }}
              style={cardStyles.main}
              bodyStyle={{ padding: '16px' }}
            >
              {deadStockData.length > 0 ? (
                <Table
                dataSource={deadStockData}
                columns={columns1}
                pagination={false}
                scroll={{ x: 200,y: 300 }}
                size="middle"
                bordered
                rowClassName={() => 'hover-row'}
              />
              ) : (
                <Empty description="No records found" />
              )}
            </Card>
          </Spin>
        </Col>
      </Row>

    </Spin>
  </div>
)
}

export default WarehouseDashboard;